import { apiClient, type Paginated } from '@/core/api';

import type {
  WikiCollection,
  WikiCollectionInput,
  WikiCollectionMember,
  WikiCollectionMemberInput,
  WikiHome,
  WikiPage,
  WikiPageInput,
  WikiPageMember,
  WikiPageMemberInput,
  WikiPageVersion,
  WikiSticky,
  WikiStickyInput,
} from './types';

// The wiki app is mounted at /api/wiki/; the routers add the resource segment.
const COLLECTIONS = '/wiki/collections/';
const COLLECTION_MEMBERS = '/wiki/collection-members/';
const PAGES = '/wiki/pages/';
const PAGE_MEMBERS = '/wiki/page-members/';
const STICKIES = '/wiki/stickies/';
const HOME = '/wiki/home/';
/** Knowledge Base RAG sync (knowledge_base app) — index a source entity. */
const KB_SYNC_SOURCE = '/knowledge-base/sync-source/';

function crud<T, TInput>(base: string) {
  return {
    list: async (params: Record<string, unknown> = {}): Promise<Paginated<T>> =>
      (await apiClient.get<Paginated<T>>(base, { params })).data,
    get: async (id: string | number): Promise<T> =>
      (await apiClient.get<T>(`${base}${id}/`)).data,
    create: async (input: TInput): Promise<T> =>
      (await apiClient.post<T>(base, input)).data,
    update: async (id: string | number, input: Partial<TInput>): Promise<T> =>
      (await apiClient.patch<T>(`${base}${id}/`, input)).data,
    remove: async (id: string | number): Promise<void> => {
      await apiClient.delete(`${base}${id}/`);
    },
  };
}

const collectionsCrud = crud<WikiCollection, WikiCollectionInput>(COLLECTIONS);
export const collectionsApi = {
  ...collectionsCrud,
  archive: async (id: string | number): Promise<WikiCollection> =>
    (await apiClient.post<WikiCollection>(`${COLLECTIONS}${id}/archive/`)).data,
  restore: async (id: string | number): Promise<WikiCollection> =>
    (await apiClient.post<WikiCollection>(`${COLLECTIONS}${id}/restore/`)).data,
  /** Set the global collection order from an ordered list of ids. */
  reorder: async (ids: number[]): Promise<WikiCollection[]> =>
    (await apiClient.post<WikiCollection[]>(`${COLLECTIONS}reorder/`, { ids })).data,
};

export const collectionMembersApi = crud<WikiCollectionMember, WikiCollectionMemberInput>(
  COLLECTION_MEMBERS,
);

const pagesCrud = crud<WikiPage, WikiPageInput>(PAGES);
export const pagesApi = {
  ...pagesCrud,
  versions: async (id: string | number): Promise<WikiPageVersion[]> =>
    (await apiClient.get<WikiPageVersion[]>(`${PAGES}${id}/versions/`)).data,
  restoreVersion: async (id: string | number, version: number): Promise<WikiPage> =>
    (await apiClient.post<WikiPage>(`${PAGES}${id}/restore-version/`, { version })).data,
  publish: async (id: string | number): Promise<WikiPage> =>
    (await apiClient.post<WikiPage>(`${PAGES}${id}/publish/`)).data,
  unpublish: async (id: string | number): Promise<WikiPage> =>
    (await apiClient.post<WikiPage>(`${PAGES}${id}/unpublish/`)).data,
  archive: async (id: string | number): Promise<WikiPage> =>
    (await apiClient.post<WikiPage>(`${PAGES}${id}/archive/`)).data,
  restore: async (id: string | number): Promise<WikiPage> =>
    (await apiClient.post<WikiPage>(`${PAGES}${id}/restore/`)).data,
  visit: async (id: string | number): Promise<void> => {
    await apiClient.post(`${PAGES}${id}/visit/`);
  },
  /** A page + its descendant subtree for the editor's page-embed (ADR-075). */
  subtree: async (
    id: string | number,
  ): Promise<{ pages: (WikiPage & { depth: number })[]; truncated: boolean }> =>
    (
      await apiClient.get<{ pages: (WikiPage & { depth: number })[]; truncated: boolean }>(
        `${PAGES}${id}/subtree/`,
      )
    ).data,
  /** Atomic drag-and-drop reorder/re-parent (one transaction; ADR-080). */
  reorder: async (
    items: { id: number; parent: number | null; order: number; visibility?: string }[],
  ): Promise<WikiPage[]> =>
    (await apiClient.post<WikiPage[]>(`${PAGES}reorder/`, { items })).data,
  /** Queue this page into the Knowledge Base RAG index. Returns 202. */
  syncToRag: async (id: number): Promise<void> => {
    await apiClient.post(KB_SYNC_SOURCE, { entity: 'wiki.wikipage', entity_id: id });
  },
};

export const pageMembersApi = crud<WikiPageMember, WikiPageMemberInput>(PAGE_MEMBERS);
export const stickiesApi = crud<WikiSticky, WikiStickyInput>(STICKIES);

export async function getWikiHome(): Promise<WikiHome> {
  const { data } = await apiClient.get<WikiHome>(HOME);
  return data;
}
